'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { preload } from 'react-dom';
import Image from 'next/image';
import Link from 'next/link';
import { applyTranslations, type Product } from '@/lib/products';
import { useTranslation } from '@/context/LanguageContext';
import ProductCard from '@/components/ProductCard';
import WaveDivider from '@/components/WaveDivider';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

const HERO_POSTER = '/media/site/hero-poster.webp';
const HERO_VIDEO = '/media/site/hero-video.mp4';
const HERO_VIDEO_START = 1;

function featuredMainImageSrc(product: Product): string {
  const base = product.image?.trim() || '';
  const card = product.cardImage?.trim() || '';
  return card || base || '/media/site/about-scan.webp';
}

// The hero poster is the mobile LCP element, so fetch it with high priority
// before the (heavy, deferred) background video.
function preloadHeroPoster() {
  preload(HERO_POSTER, { as: 'image', fetchPriority: 'high' });
}

export default function HomeContent({ initialProducts }: { initialProducts: Product[] }) {
  preloadHeroPoster();
  const { t } = useTranslation();
  const [products] = useState<Product[]>(initialProducts);
  const [heroVideoSrc, setHeroVideoSrc] = useState<string | null>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  // Defer the 11MB background video until the page has finished its critical
  // work, and skip it entirely on data-saver / slow connections. The poster
  // image carries the hero until (and unless) the video loads, so LCP no
  // longer competes with the video download.
  useEffect(() => {
    const nav = navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    };
    const conn = nav.connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /(^|-)2g$/.test(conn.effectiveType)) return;

    let idleId = 0;
    const start = () => {
      const ric =
        (window as Window & { requestIdleCallback?: (cb: () => void) => number })
          .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1));
      idleId = ric(() => setHeroVideoSrc(HERO_VIDEO));
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
    return () => {
      window.removeEventListener('load', start);
      if (idleId) {
        const cic = (window as Window & { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback;
        cic?.(idleId);
      }
    };
  }, []);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video || !heroVideoSrc) return;
    // iOS/mobile autoplay requires the video to be muted and play inline.
    // React doesn't reliably set the `muted` attribute, so enforce it here.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    // The first second of the clip is too "wild", so skip it on start and on
    // every loop.
    const seekToStart = () => {
      try {
        if (video.currentTime < HERO_VIDEO_START) {
          video.currentTime = HERO_VIDEO_START;
        }
      } catch {}
    };
    const tryPlay = () => {
      seekToStart();
      const result = video.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }
    };
    // Manual loop so playback restarts at HERO_VIDEO_START instead of 0.
    const restart = () => {
      try {
        video.currentTime = HERO_VIDEO_START;
      } catch {}
      tryPlay();
    };
    tryPlay();
    video.addEventListener('loadedmetadata', seekToStart);
    video.addEventListener('canplay', tryPlay, { once: true });
    video.addEventListener('ended', restart);
    return () => {
      video.removeEventListener('loadedmetadata', seekToStart);
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('ended', restart);
    };
  }, [heroVideoSrc]);

  useEffect(() => {
    const layer = parallaxRef.current;
    const section = layer?.parentElement;
    if (!layer || !section) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      // -1 when the section sits below the viewport, +1 when above it.
      const progress = (rect.top + rect.height / 2 - viewportH / 2) / viewportH;
      const shift = Math.max(-40, Math.min(40, progress * -40));
      layer.style.transform = `translate3d(0, ${shift}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const translatedProducts = useMemo(
    () => products.map((p) => applyTranslations(p, t)),
    [products, t]
  );
  const featured = translatedProducts.find((p) => p.featured) ?? translatedProducts[0];
  const heroTitleLines = t.home.hero.title.split('\n');

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroVideoWrap}>
          <video
            ref={heroVideoRef}
            className={styles.heroVideo}
            autoPlay
            muted
            playsInline
            preload="none"
            poster={HERO_POSTER}
          >
            {heroVideoSrc && <source src={heroVideoSrc} type="video/mp4" />}
          </video>
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {heroTitleLines.map((line, index) => (
              <span key={index}>
                {line}
                {index < heroTitleLines.length - 1 && <br />}
              </span>
            ))}
          </h1>
          <p className={styles.heroSub}>
            {t.home.hero.subtitle}
          </p>
          <Link href="/shop" className={styles.heroCta}>
            {t.home.hero.cta}
          </Link>
        </div>
        <div className={styles.scrollHint} aria-hidden="true">
          <span />
        </div>
      </section>

      <DoubleWaveDivider flip />

      <section className={styles.parallaxBreak} aria-label={t.home.mosaic.alt1}>
        <div ref={parallaxRef} className={styles.parallaxImage} aria-hidden="true" />
      </section>

      <DoubleWaveDivider />

      {featured && (
        <section className={`${styles.featured} section section--magenta`}>
          <div className="container">
            <div className={styles.featuredGrid}>
              <div className={styles.featuredImage}>
                <Image
                  src={featuredMainImageSrc(featured)}
                  alt={featured.name}
                  width={1200}
                  height={1200}
                  className={styles.featuredVideo}
                />
              </div>
              <div className={styles.featuredText}>
                <p className={styles.featuredEyebrow}>
                  {featured.featureSubheading?.trim() || t.home.featured.eyebrow}
                </p>
                <h2 className={styles.featuredName}>
                  {featured.featureHeading?.trim() || featured.name}
                </h2>
                <p className={styles.featuredFlavor}>{featured.flavor}</p>
                <p className={styles.featuredDesc}>
                  {featured.featureParagraph?.trim() || featured.description}
                </p>
                <Link href={`/shop/${featured.slug}`} className={styles.featuredCta}>
                  {t.home.featured.cta}
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <WaveDivider from="magenta" />

      <section className={styles.products}>
        <div className="container">
          <p className={styles.productsEyebrow}>{t.home.products.eyebrow}</p>
          <h2 className={styles.productsTitle}>{t.home.products.title}</h2>
          <div className={styles.grid}>
            {translatedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className={styles.quote}>
        <div className="container">
          <blockquote className={styles.quoteText}>
            &ldquo;{t.home.quote.text}&rdquo;
          </blockquote>
          <p className={styles.quoteAttr}>{t.home.quote.attribution}</p>
        </div>
      </section>

      <WaveDivider from="yellow" />

      <section className={`${styles.howItWorks} section section--magenta`}>
        <div className="container">
          <p className={styles.hiwEyebrow}>{t.home.howItWorks.eyebrow}</p>
          <h2 className={styles.hiwTitle}>{t.home.howItWorks.title}</h2>
          <div className={styles.hiwGrid}>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>01</span>
              <h3>{t.home.howItWorks.step1Title}</h3>
              <p>{t.home.howItWorks.step1Body}</p>
            </div>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>02</span>
              <h3>{t.home.howItWorks.step2Title}</h3>
              <p>{t.home.howItWorks.step2Body}</p>
            </div>
            <div className={styles.hiwStep}>
              <span className={styles.hiwNumber}>03</span>
              <h3>{t.home.howItWorks.step3Title}</h3>
              <p>{t.home.howItWorks.step3Body}</p>
            </div>
          </div>
          <div className={styles.hiwCta}>
            <Link href="/abonnement" className={styles.hiwBtn}>
              {t.home.howItWorks.cta}
            </Link>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={styles.finalCta}>
        <div className="container">
          <h2 className={styles.finalCtaTitle}>{t.home.finalCta.title}</h2>
          <p className={styles.finalCtaSub}>
            {t.home.finalCta.body}
          </p>
          <div className={styles.finalCtaButtons}>
            <Link href="/shop" className={styles.ctaBtnPrimary}>
              {t.home.finalCta.primaryCta}
            </Link>
            <Link href="/om" className={styles.ctaBtnOutline}>
              {t.home.finalCta.secondaryCta}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
